import { formatPrice, formatUnit } from '@lichtblick/localization';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { Footnote } from '@lichtblick/photon/molecules/footnote/footnote';
import { BonusParagraph, BonusPriceSection } from '@/components/tariff-details-layer/bonus';
import { LegalInformation } from '@/components/tariff-details-layer/legal-information';
import { ModalHeadline } from '@/components/tariff-details-layer/modal-headline';
import { PriceWrapper } from '@/components/tariff-details-layer/price-wrapper';
import { calculateWorkPrice } from '@/components/tariff-details-layer/tariff-details-layer.helpers';
import { TariffDetailsModal } from '@/components/tariff-details-layer/tariff-details-modal';
import { i18n } from '../../i18n';
import type { TariffsQuery } from '../../types/generated';
import { PriceBreakdownList } from '../price-breakdown/price-breakdown-list';
import { PriceBreakdownRow } from '../price-breakdown/price-breakdown-row';
import { MonthlyPrices } from './monthly-prices';

type GasTariffDetailsLayerProps = {
  index: number;
  isComboCheckout: boolean;
  product: TariffsQuery['tariffs'][number];
};

export const GasTariffDetailsLayer: React.FC<GasTariffDetailsLayerProps> = ({
  index = -1,
  isComboCheckout,
  product,
}) => {
  return (
    <TariffDetailsModal index={index} isComboCheckout={isComboCheckout} product={product}>
      <ModalHeadline>{product.name} - Tarifdetails</ModalHeadline>

      <MonthlyPrices product={product} />

      <PriceWrapper>
        <Text bold size="l">
          Voraussichtliche Kosten
        </Text>

        <PriceBreakdownList>
          <PriceBreakdownRow label="Laufzeit" value={`${product.contractPeriod?.value} Monate`} />
          <PriceBreakdownRow label="Energiepreisgarantie¹" value={`${product.priceGuarantee?.value} Monate`} />
          <PriceBreakdownRow label="Arbeitspreis" value={formatPrice(product.workPrice, 'ct/kWh')} />
          <PriceBreakdownRow label="Monatl. Grundpreis" value={formatPrice(product.monthlyBasePrice)} />
        </PriceBreakdownList>

        <PriceBreakdownList>
          {product.workPriceNt ? (
            <>
              <PriceBreakdownRow
                label={`Jährlicher Arbeitspreis HT (bei ${formatUnit(product.usageHt, 'kWh')})`}
                value={formatPrice(calculateWorkPrice(product.workPrice ?? 0, product.usageHt ?? 0))}
              />
              <PriceBreakdownRow
                label={`Jährlicher Arbeitspreis NT (bei ${formatUnit(product.usageNt, 'kWh')})`}
                value={`+${formatPrice(calculateWorkPrice(product.workPriceNt ?? 0, product.usageNt ?? 0))}`}
              />
            </>
          ) : (
            <PriceBreakdownRow
              label={`Jährlicher Arbeitspreis (bei ${formatUnit(product.usage, 'kWh')})`}
              value={formatPrice(product.totalWorkPrice)}
            />
          )}

          <PriceBreakdownRow label="Jährlicher Grundpreis" value={`+${formatPrice(product.basePrice)}`} />

          <PriceBreakdownRow
            isBold
            label="Kosten im Jahr (ohne Bonus)"
            value={formatPrice(product.totalPriceNoBonus)}
          />
          <PriceBreakdownRow isBold label="Abschlag pro Monat (ohne Bonus)" value={formatPrice(product.monthlyRate)} />
        </PriceBreakdownList>

        <BonusPriceSection product={product} />
      </PriceWrapper>

      <BonusParagraph product={product} />

      <LegalInformation product={product} />

      <Footnote index="1">{i18n.footnotes.gasPriceGuarantee}</Footnote>
    </TariffDetailsModal>
  );
};
